import type {
  DocumentType,
  UploadResponse,
  ClientKYCStatusResponse,
  KYCDocument,
  DeleteResponse,
  DocumentUrlResponse,
} from './types';
import { getApiBaseUrl } from '@/lib/env';
import { apiClient } from '@/sdk/shared/apiClient';

export const clientKycApi = {
  // Upload a KYC document
  uploadDocument: async (
    file: File,
    tenantSlug: string,
    clientId: string,
    documentType: DocumentType,
    onProgress?: (progress: number) => void
  ): Promise<UploadResponse> => {
    return new Promise((resolve, reject) => {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('tenantSlug', tenantSlug);
      formData.append('clientId', clientId);
      formData.append('documentType', documentType);

      const xhr = new XMLHttpRequest();

      xhr.upload.addEventListener('progress', (event) => {
        if (event.lengthComputable && onProgress) {
          const progress = Math.round((event.loaded / event.total) * 100);
          onProgress(progress);
        }
      });

      xhr.addEventListener('load', () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          const response = JSON.parse(xhr.responseText);
          resolve(response.data);
        } else {
          const error = JSON.parse(xhr.responseText);
          reject(new Error(error.error || 'Upload failed'));
        }
      });

      xhr.addEventListener('error', () => reject(new Error('Network error during upload')));

      xhr.open('POST', `${getApiBaseUrl()}/kyc/upload`);
      const token = apiClient.getToken();
      if (token) {
        xhr.setRequestHeader('Authorization', `Bearer ${token}`);
      }
      xhr.send(formData);
    });
  },

  // Get client KYC status
  getKYCStatus: async (
    clientId: string,
    tenantSlug: string
  ): Promise<ClientKYCStatusResponse> => {
    const token = apiClient.getToken();
    const res = await fetch(
      `${getApiBaseUrl()}/kyc/status/${clientId}?tenantSlug=${encodeURIComponent(tenantSlug)}`,
      {
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      }
    );
    if (!res.ok) {
      const error = await res.json();
      throw new Error(error.error || 'Failed to fetch KYC status');
    }
    const response = await res.json();
    return response.data;
  },

  // Get client documents
  getClientDocuments: async (
    clientId: string,
    tenantSlug: string
  ): Promise<KYCDocument[]> => {
    const token = apiClient.getToken();
    const res = await fetch(
      `${getApiBaseUrl()}/kyc/documents/${clientId}?tenantSlug=${encodeURIComponent(tenantSlug)}`,
      {
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      }
    );
    if (!res.ok) {
      const error = await res.json();
      throw new Error(error.error || 'Failed to fetch client documents');
    }
    const response = await res.json();
    return response.data;
  },

  // Delete a document
  deleteDocument: async (
    documentId: string,
    tenantSlug: string,
    clientId: string
  ): Promise<DeleteResponse> => {
    const token = apiClient.getToken();
    const res = await fetch(
      `${getApiBaseUrl()}/kyc/${documentId}?tenantSlug=${encodeURIComponent(tenantSlug)}`,
      {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ clientId }),
      }
    );
    if (!res.ok) {
      const error = await res.json();
      throw new Error(error.error || 'Failed to delete document');
    }
    const response = await res.json();
    return response.data;
  },

  // Get signed URL for document
  getDocumentUrl: async (
    documentId: string,
    tenantSlug: string
  ): Promise<DocumentUrlResponse> => {
    const token = apiClient.getToken();
    const res = await fetch(
      `${getApiBaseUrl()}/kyc/${documentId}/url?tenantSlug=${encodeURIComponent(tenantSlug)}`,
      {
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      }
    );
    if (!res.ok) {
      const error = await res.json();
      throw new Error(error.error || 'Failed to get document URL');
    }
    const response = await res.json();
    return response.data;
  },

  // Get document image (authenticated proxy)
  getDocumentImage: async (
    documentId: string,
    tenantSlug: string
  ): Promise<Blob> => {
    const token = apiClient.getToken();
    const res = await fetch(
      `${getApiBaseUrl()}/kyc/${documentId}/image?tenantSlug=${encodeURIComponent(tenantSlug)}`,
      {
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      }
    );
    if (!res.ok) {
      const error = await res.json();
      throw new Error(error.error || 'Failed to get document image');
    }
    return res.blob();
  },

  // Get proxy stream URL for document (when signed URL is not available)
  getProxyStreamUrl: (documentId: string, tenantSlug: string): string => {
    return `${getApiBaseUrl()}/kyc/${documentId}/stream?tenantSlug=${encodeURIComponent(tenantSlug)}`;
  },
};

export default clientKycApi;

// Standalone exported functions for convenience
export const getDocumentUrl = clientKycApi.getDocumentUrl;
export const getProxyStreamUrl = clientKycApi.getProxyStreamUrl;
